import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box, Text, Link, Divider
} from 'components/ui';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    fontSizes, fontWeights, colors, space
} from 'style/config';
import bccUtils from 'utils/BCC';
import { renderModal, globalModals } from 'utils/globalModals';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import Actions from 'Actions';
import urlUtils from 'utils/Url';
import localeUtils from 'utils/LanguageLocale';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import SameDayUnlimitedBindings from 'analytics/bindingMethods/pages/myAccount/SameDayUnlimitedBindings';
import FeesAndFAQEN from 'components/ProductPage/Type/DigitalProduct/SameDayUnlimitedLandingPage/FeesAndFAQ/locales/FeesAndFAQ_en_US';
import FeesAndFAQFR from 'components/ProductPage/Type/DigitalProduct/SameDayUnlimitedLandingPage/FeesAndFAQ/locales/FeesAndFAQ_fr_CA';

const { SDU_SERVICE_FAQS } = globalModals;
const { SAME_DAY_UNLIMITED_FAQ } = bccUtils.MEDIA_IDS;
const getText = localeUtils.getLocalisedDataFromResource(FeesAndFAQEN, FeesAndFAQFR);

// Pure helpers, hoisted out of the component (no hook deps)
const redirectToFAQ = () => urlUtils.redirectTo('/beauty/same-day-unlimited-faq');
const FAQClick = () => {
    SameDayUnlimitedBindings.FAQClick();
};
const fireAnalytics = openInModal => {
    const pageType = openInModal ? anaConsts.PAGE_TYPES.SAME_DAY_UNLIMITED : anaConsts.PAGE_TYPES.CONTENT_STORE;
    const pageDetail = openInModal ? anaConsts.PAGE_DETAIL.SAME_DAY_UNLIMITED_FAQ_MODAL : anaConsts.PAGE_DETAIL.SAME_DAY_UNLIMITED_FAQ_PAGE;
    const data = {
        pageName: `${pageType}:${pageDetail}:n/a:*`,
        pageType,
        pageDetail
    };

    if (openInModal) {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data });
    } else {
        anaUtils.setNextPageData(data);
    }
};

const FeesAndFAQ = ({ alignLeft, openInModal, hideAsteriscSection }) => {
    const dispatch = useDispatch();
    const globalModalsData = useSelector(globalModalsSelector);
    const showMediaModal = useCallback(opts => dispatch(Actions.showMediaModal(opts)), [dispatch]);

    const taxes = getText('plusApplicableTaxes');
    const fees = getText('feesMayApply');
    const needHelp = getText('needHelp');
    const viewFaq = getText('viewFaqs');

    const clickHandler = () => {
        fireAnalytics(openInModal);
        FAQClick();

        if (openInModal) {
            renderModal(globalModalsData[SDU_SERVICE_FAQS], () => {
                showMediaModal({
                    isOpen: true,
                    mediaId: SAME_DAY_UNLIMITED_FAQ,
                    titleDataAt: 'auto-replenishment'
                });
            });
        } else {
            redirectToFAQ();
        }
    };

    return (
        <Box css={styles.container}>
            {hideAsteriscSection || (
                <Box css={styles.smallText}>
                    <Text is='p'>
                        <sup>*</sup>
                        {taxes}
                    </Text>
                    <Text
                        is='p'
                        css={styles.fees}
                    >
                        <sup>**</sup>
                        {fees}
                    </Text>
                </Box>
            )}
            <Divider css={styles.divider} />
            <Box>
                <Text
                    is='p'
                    css={`
                        ${alignLeft ? { ...styles.needHelp, ...styles.left } : styles.needHelp}
                    `}
                >
                    {`${needHelp} `}
                    <Link
                        css={styles.link}
                        onClick={clickHandler}
                    >
                        {viewFaq}
                    </Link>
                </Text>
            </Box>
        </Box>
    );
};

const styles = {
    container: {
        marginBottom: `-${space[1]}px`
    },
    smallText: {
        fontSize: fontSizes.sm,
        color: colors.gray
    },
    fees: {
        marginTop: space[3]
    },
    divider: {
        marginTop: space[4],
        marginBottom: space[4]
    },
    needHelp: {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.normal,
        textAlign: 'center'
    },
    left: {
        textAlign: 'left'
    },
    link: {
        color: colors.blue
    }
};

FeesAndFAQ.propTypes = {
    alignLeft: PropTypes.bool,
    openInModal: PropTypes.bool,
    hideAsteriscSection: PropTypes.bool
};

export default wrapFunctionalComponent(FeesAndFAQ, 'FeesAndFAQ');
